import {TradingIdeasService} from "../service/TradingIdeasService";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {TradeIdeaRecords} from "../models/TradeIdeaRecord";
import {BuildModel, orderByConfidence, TradeIdeasViewModel, TradeIdeasViewModels} from "../models/TradeIdeasViewModel";
import {TradeIdeaRiskViewModel} from "../models/TradeIdeaRiskViewModel";
import {ArrayUtil} from "../../js/util/ArrayUtil";

const RiskIncrement = 500;
const MaxRisk = 10000;

export class TradingIdeasViewStore {

    private readonly models$ = new ReplaySubject<TradeIdeasViewModels>(1);
    private readonly ideasCount$ = new ReplaySubject<number>(1);

    private readonly currentIdeaIndex$ = new ReplaySubject<number>(1);
    private readonly hasNextIdea$ = new ReplaySubject<boolean>(1);
    private readonly hasPreviousIdea$ = new ReplaySubject<boolean>(1);
    private readonly hasIdeas$ = new ReplaySubject<boolean>(1);
    private readonly hasMultipleIdeas$ = new ReplaySubject<boolean>(1);
    private readonly currentModel$ = new ReplaySubject<TradeIdeasViewModel>(1);
    private readonly currentRiskModel$ = new ReplaySubject<TradeIdeaRiskViewModel>(1);

    private readonly nextIdea$ = new ReplaySubject<boolean>(1);
    private readonly previousIdea$ = new ReplaySubject<boolean>(1);

    private readonly updateLots$ = new ReplaySubject<number>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public constructor(private readonly service: TradingIdeasService) {
        this.init();
    }

    private init() {
        this.watchIdeaCount();
        this.watchCountSource();
        this.watchForHasNext();
        this.watchForHasPrevious();
        this.watchCurrentIndex();
        this.watchCurrentModel();
        this.watchNextIdea();
        this.watchPreviousIdea();
        this.watchUpdateLots();
        this.fetchData();
    }

    private watchIdeaCount() {
        this.ideasCount$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((ideas) => ideas > 1)
            .subscribe((hasMultiple) => this.hasMultipleIdeas$.next(hasMultiple));

        this.ideasCount$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((ideas) => ideas > 0)
            .subscribe((hasIdeas) => this.hasIdeas$.next(hasIdeas));
    }

    private watchCountSource() {
        this.models$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((models) => models.length)
            .subscribe((count) => this.ideasCount$.next(count));
    }

    private watchForHasNext() {
        const source$ = Observable.combineLatest(this.currentIdeaIndex$, this.ideasCount$);
        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([current, count]) => current < (count - 1))
               .subscribe((hasNext) => this.hasNextIdea$.next(hasNext));
    }

    private watchForHasPrevious() {
        this.currentIdeaIndex$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((current) => current > 0)
            .subscribe((hasNext) => this.hasPreviousIdea$.next(hasNext));
    }

    private watchCurrentIndex() {
        const source$ = Observable.combineLatest(this.currentIdeaIndex$, this.models$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(([index, models]) => models[index])
               .subscribe((model) => this.currentModel$.next(model));
    }

    private watchCurrentModel() {
        this.currentModel$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map((model) => model.riskAmount)
            .subscribe((risk) => this.currentRiskModel$.next(risk));
    }

    private watchNextIdea() {
        this.nextIdea$
            .withLatestFrom(this.currentIdeaIndex$, this.hasNextIdea$)
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .filter(([_, index, hasNext]) => hasNext)
            .map(([_, index]) => index + 1)
            .subscribe((index) => this.currentIdeaIndex$.next(index));
    }

    private watchPreviousIdea() {
        this.previousIdea$
            .withLatestFrom(this.currentIdeaIndex$, this.hasPreviousIdea$)
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .filter(([_, index, hasPrevious]) => hasPrevious)
            .map(([_, index]) => index - 1)
            .subscribe((index) => this.currentIdeaIndex$.next(index));
    }

    private watchUpdateLots() {
        this.updateLots$
            .withLatestFrom(this.currentModel$, this.models$)
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .map(([lots, currentModel, models]) => this.updateModelLots(lots, currentModel, models))
            .subscribe((model) => this.models$.next(model));
    }

    private updateModelLots(lots: number,
                            currentModel: TradeIdeasViewModel,
                            models: TradeIdeasViewModels): TradeIdeasViewModels {
        const updatedModel = currentModel.updateLots(lots);
        return ArrayUtil.replaceBy(models, updatedModel, (m) => m.isSameIdea(updatedModel));
    }

    private fetchData() {
        const request = this.service.list();
        Observable.fromPromise(request)
                  .map((ideas) => this.buildModels(ideas))
                  .subscribe((models) => this.setInitialModelData(models),
                             (err) => this.handleRetrieveError(err));
    }

    private setInitialModelData(models: TradeIdeasViewModels) {
        this.models$.next(models);
        if (models.length > 0) {
            this.currentIdeaIndex$.next(0);
        }
    }

    /*
     * If we get an error then just pretend to the user that there are no current ideas
     * so they don't get a completely blank component hanging around
     */
    private handleRetrieveError(error: Error) {
        console.error(error);
        this.models$.next([]);
    }

    private buildModels(ideas: TradeIdeaRecords): TradeIdeasViewModels {
        return ideas.map((i) => BuildModel(i, RiskIncrement, MaxRisk))
                    .sort(orderByConfidence());
    }

    public nextIdea() {
        this.nextIdea$.next(true);
    }

    public previousIdea() {
        this.previousIdea$.next(true);
    }

    public updateLots(lots: number) {
        this.updateLots$.next(lots);
    }

    public get hasIdeas(): Observable<boolean> {
        return this.hasIdeas$.observeOn(Scheduler.async);
    }

    public get hasMultipleIdeas(): Observable<boolean> {
        return this.hasMultipleIdeas$.observeOn(Scheduler.async);
    }

    public get hasNextIdea(): Observable<boolean> {
        return this.hasNextIdea$.observeOn(Scheduler.async);
    }

    public get hasPreviousIdea(): Observable<boolean> {
        return this.hasPreviousIdea$.observeOn(Scheduler.async);
    }

    public get currentModel(): Observable<TradeIdeasViewModel> {
        return this.currentModel$.observeOn(Scheduler.async);
    }

    public get currentRisk(): Observable<TradeIdeaRiskViewModel> {
        return this.currentRiskModel$.observeOn(Scheduler.async);
    }

    public destroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
